import { Draft, produce } from "immer";

export type TStructuredSectionPathNode<TSection> = Omit<
  TSection,
  "path" | "sections"
> & {
  path: string;
  sections: TStructuredSectionPathNode<TSection>[];
};

// Build the UI-only section path that identifies a section by its position in the tree.
export const getStructuredSectionPath = (
  parentPath: string | undefined,
  index: number,
) => (parentPath ? `${parentPath}.${index}` : index.toString());

// Convert a section path string like "0.1.0" into indexes for array/form traversal.
export const parseStructuredSectionPath = (path: string): number[] => {
  if (!path) {
    return [];
  }

  const pathIndexes = path.split(".").map((pathPart) => Number(pathPart));

  return pathIndexes.every(
    (pathIndex) => Number.isInteger(pathIndex) && pathIndex >= 0,
  )
    ? pathIndexes
    : [];
};

// Add UI-only section paths in a single tree pass.
export const addStructuredSectionPaths = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[] = [],
  parentPath?: string,
): TStructuredSectionPathNode<TSection>[] =>
  sections.map((section, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    return {
      ...section,
      path,
      sections: addStructuredSectionPaths(section.sections ?? [], path),
    };
  });

// Collect every section path in pre-order without allocating intermediate nested arrays.
export const getStructuredSectionPaths = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[] = [],
  parentPath?: string,
  sectionPaths: string[] = [],
): string[] => {
  sections.forEach((section, index) => {
    const path = getStructuredSectionPath(parentPath, index);

    sectionPaths.push(path);
    getStructuredSectionPaths(section.sections ?? [], path, sectionPaths);
  });

  return sectionPaths;
};

// Count sections.
export const getStructuredSectionCount = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[] = [],
): number => {
  let count = 0;
  const sectionsToCount = [...sections];

  while (sectionsToCount.length > 0) {
    const section = sectionsToCount.pop()!;

    count += 1;

    if (section.sections?.length) {
      sectionsToCount.push(...section.sections);
    }
  }

  return count;
};

// Find a section by UI path without relying on backend ids.
export const getStructuredSectionByPath = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[],
  path: string,
): TSection | undefined => {
  let sectionList = sections;
  const pathIndexes = parseStructuredSectionPath(path);

  for (const [depth, pathIndex] of pathIndexes.entries()) {
    const section = sectionList[pathIndex];

    if (!section) {
      return;
    }

    if (depth === pathIndexes.length - 1) {
      return section;
    }

    sectionList = section.sections ?? [];
  }

  return;
};

// Mutate one section by UI path while preserving references for unchanged branches.
export const mutateStructuredSectionByPath = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[] = [],
  path: string,
  recipe: (section: Draft<TSection>) => void,
): TSection[] => {
  const pathIndexes = parseStructuredSectionPath(path);

  return produce(sections, (draftSections) => {
    let currentSections = draftSections;

    for (const [depth, pathIndex] of pathIndexes.entries()) {
      const section = currentSections[pathIndex];

      if (!section) {
        return;
      }

      if (depth === pathIndexes.length - 1) {
        recipe(section);

        return;
      }

      currentSections = section.sections ?? [];
    }
  });
};

// Delete one section by UI path while preserving references for unchanged branches.
export const deleteStructuredSectionByPath = <
  TSection extends { sections?: TSection[] },
>(
  sections: TSection[] = [],
  path: string,
): TSection[] => {
  const pathIndexes = parseStructuredSectionPath(path);

  return produce(sections, (draftSections) => {
    let currentSections = draftSections;

    for (const [depth, pathIndex] of pathIndexes.entries()) {
      const section = currentSections[pathIndex];

      if (!section) {
        return;
      }

      if (depth === pathIndexes.length - 1) {
        currentSections.splice(pathIndex, 1);

        return;
      }

      currentSections = section.sections ?? [];
    }
  });
};
